/* ============================================================
   Classy Kitchen — Premium Product Page
   assets/premium-product.js
   Vanilla JS only. No jQuery. No frameworks.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Boot once per section instance ---------- */
  document.querySelectorAll('.ck-product').forEach(initProductSection);

  function initProductSection(root) {
    if (root.dataset.ckInit) return;
    root.dataset.ckInit = 'true';

    let productData = {};
    try {
      productData = JSON.parse(root.dataset.productJson || '{}');
    } catch (e) {
      productData = {};
    }

    initGallery(root);
    initWishlist(root);
    initVariantPicker(root, productData);
    initQuantityStepper(root);
    initAddToCart(root);
    initStickyBar(root);
    initTabs(root);
    initMobileAccordion(root);
    initFaq(root);
    initPincodeChecker(root);
    initRipple(root);
    initFadeInObserver(root);
    initRelatedSliders(root);
    initRecentlyViewed(root, productData);
  }

  /* ============================================================
     GALLERY: thumbnails, prev/next, counter, zoom, lazy load
     ============================================================ */
  function initGallery(root) {
    const stage = root.querySelector('[data-ck-stage]');
    if (!stage) return;

    const slides = Array.from(stage.querySelectorAll('[data-ck-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-ck-thumb]'));
    const prevBtn = root.querySelector('[data-ck-prev]');
    const nextBtn = root.querySelector('[data-ck-next]');
    const currentEl = root.querySelector('[data-ck-current]');
    let index = 0;

    function show(i) {
      if (i < 0) i = slides.length - 1;
      if (i >= slides.length) i = 0;
      index = i;

      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === index));
      thumbs.forEach((t, idx) => {
        t.classList.toggle('is-active', idx === index);
        t.setAttribute('aria-selected', idx === index ? 'true' : 'false');
      });
      if (currentEl) currentEl.textContent = index + 1;

      // pause any videos not visible
      slides.forEach((s, idx) => {
        const video = s.querySelector('video');
        if (video && idx !== index) video.pause();
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(parseInt(thumb.dataset.index, 10)));
    });
    if (prevBtn) prevBtn.addEventListener('click', () => show(index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => show(index + 1));

    // keyboard support
    stage.setAttribute('tabindex', '0');
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });

    // swipe support
    let touchStartX = 0;
    stage.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      const diff = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(diff) > 40) show(diff > 0 ? index - 1 : index + 1);
    }, { passive: true });

    // hover zoom (desktop): track pointer position for a focal zoom
    stage.querySelectorAll('[data-ck-zoom]').forEach((zoomEl) => {
      const img = zoomEl.querySelector('img');
      if (!img) return;
      zoomEl.addEventListener('mousemove', (e) => {
        const rect = zoomEl.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      zoomEl.addEventListener('mouseleave', () => {
        img.style.transformOrigin = 'center center';
      });
    });
  }

  /* ============================================================
     WISHLIST
     ============================================================ */
  function initWishlist(root) {
    const btn = root.querySelector('[data-ck-wishlist]');
    if (!btn) return;
    const handle = root.dataset.productHandle;
    const key = 'ck_wishlist';

    function getWishlist() {
      try { return JSON.parse(localStorage.getItem(key) || '[]'); }
      catch (e) { return []; }
    }
    function setActive() {
      const list = getWishlist();
      btn.classList.toggle('is-active', list.includes(handle));
    }
    setActive();

    btn.addEventListener('click', () => {
      let list = getWishlist();
      if (list.includes(handle)) {
        list = list.filter((h) => h !== handle);
      } else {
        list.push(handle);
      }
      try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
      setActive();
    });
  }

  /* ============================================================
     VARIANT PICKER
     ============================================================ */
  function initVariantPicker(root, productData) {
    const optionSelectors = Array.from(root.querySelectorAll('[data-ck-option-selector]'));
    if (!optionSelectors.length) return;

    const nativeSelect = root.querySelector('[data-ck-native-select]');
    const hiddenIdInput = root.querySelector('[data-ck-variant-id]');
    const priceEl = root.querySelector('[data-ck-price]');
    const compareEl = root.querySelector('[data-ck-compare-price]');
    const discountEl = root.querySelector('[data-ck-discount-badge]');
    const stockEl = root.querySelector('[data-ck-stock-status]');
    const addBtn = root.querySelector('[data-ck-add-to-cart]');
    const addLabel = root.querySelector('[data-ck-add-label]');
    const buyBtn = root.querySelector('[data-ck-buy-now]');
    const stickyPrice = root.querySelector('[data-ck-sticky-price]');

    const variants = productData.variants || [];
    const selections = {};

    // seed current selections from the "is-selected" pill in each option group
    root.querySelectorAll('[data-ck-option]').forEach((group) => {
      const pos = group.dataset.optionPosition;
      const selected = group.querySelector('.is-selected');
      if (selected) selections[pos] = selected.dataset.value;
    });

    function findVariant() {
      return variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return Object.keys(selections).every((pos) => {
          const idx = parseInt(pos, 10) - 1;
          return opts[idx] === selections[pos];
        });
      });
    }

    function formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return '₹' + amount.replace(/\.00$/, '');
    }

    function updateUI(variant) {
      if (!variant) return;

      if (hiddenIdInput) hiddenIdInput.value = variant.id;
      if (nativeSelect) nativeSelect.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (compareEl) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        }
        if (discountEl) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountEl.textContent = pct + '% OFF';
          discountEl.hidden = false;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (discountEl) discountEl.hidden = true;
      }

      if (stockEl) {
        stockEl.innerHTML = variant.available
          ? '<span class="ck-dot ck-dot--in"></span> In stock, ready to ship'
          : '<span class="ck-dot ck-dot--out"></span> Currently out of stock';
      }

      [addBtn, buyBtn].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !variant.available;
      });
      if (addLabel) addLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

      // update option value labels
      root.querySelectorAll('[data-ck-option]').forEach((group) => {
        const pos = group.dataset.optionPosition;
        const valueEl = group.querySelector('[data-ck-option-value]');
        if (valueEl && selections[pos]) valueEl.textContent = selections[pos];
      });
    }

    optionSelectors.forEach((pill) => {
      pill.addEventListener('click', () => {
        const group = pill.closest('[data-ck-option]');
        const pos = pill.dataset.optionPosition;

        group.querySelectorAll('[data-ck-option-selector]').forEach((p) => {
          p.classList.remove('is-selected');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('is-selected');
        pill.setAttribute('aria-checked', 'true');

        selections[pos] = pill.dataset.value;
        const variant = findVariant();
        if (variant) updateUI(variant);
      });
    });
  }

  /* ============================================================
     QUANTITY STEPPER
     ============================================================ */
  function initQuantityStepper(root) {
    const input = root.querySelector('[data-ck-qty-input]');
    const minus = root.querySelector('[data-ck-qty-minus]');
    const plus = root.querySelector('[data-ck-qty-plus]');
    if (!input) return;

    minus && minus.addEventListener('click', () => {
      const val = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      input.value = val;
    });
    plus && plus.addEventListener('click', () => {
      const val = (parseInt(input.value, 10) || 1) + 1;
      input.value = val;
    });
    input.addEventListener('change', () => {
      if (!input.value || parseInt(input.value, 10) < 1) input.value = 1;
    });
  }

  /* ============================================================
     ADD TO CART / BUY NOW  (Shopify AJAX Cart API)
     ============================================================ */
  function initAddToCart(root) {
    const form = root.querySelector('#ck-product-form') || root.querySelector('.ck-form');
    const buyBtn = root.querySelector('[data-ck-buy-now]');
    const stickyAddBtn = root.querySelector('[data-ck-sticky-add]');
    const stickyBuyBtn = root.querySelector('[data-ck-sticky-buy]');

    function getFormData() {
      const idInput = root.querySelector('[data-ck-variant-id]');
      const qtyInput = root.querySelector('[data-ck-qty-input]');
      return {
        id: idInput ? idInput.value : null,
        quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
      };
    }

    function addToCart(redirectToCheckout) {
      const data = getFormData();
      if (!data.id) return;

      fetch(window.Shopify ? '/cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      })
        .then((res) => res.json())
        .then(() => {
          if (redirectToCheckout) {
            window.location.href = '/checkout';
          } else {
            document.dispatchEvent(new CustomEvent('ck:cart:added', { detail: data }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        })
        .catch(() => {
          // Fallback: submit the native form so Shopify handles it server-side
          if (form) form.submit();
        });
    }

    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        addToCart(false);
      });
    }

    if (buyBtn) buyBtn.addEventListener('click', () => addToCart(true));
    if (stickyAddBtn) stickyAddBtn.addEventListener('click', () => addToCart(false));
    if (stickyBuyBtn) stickyBuyBtn.addEventListener('click', () => addToCart(true));
  }

  /* ============================================================
     STICKY ADD TO CART BAR — Intersection Observer, no scroll spam
     ============================================================ */
  function initStickyBar(root) {
    const bar = root.querySelector('[data-ck-sticky-bar]');
    const hero = root.querySelector('.ck-hero');
    if (!bar || !hero) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.hidden = false;
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0, rootMargin: '0px' });

    observer.observe(hero);
  }

  /* ============================================================
     TABS
     ============================================================ */
  function initTabs(root) {
    const nav = root.querySelector('[data-ck-tabs-nav]');
    if (!nav) return;

    const buttons = Array.from(nav.querySelectorAll('[data-ck-tab-btn]'));
    const panels = Array.from(root.querySelectorAll('[data-ck-tab-panel]'));

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.ckTabBtn;

        buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.ckTabPanel === target));
      });
    });
  }


function initMobileAccordion(root) {

    // Desktop: do nothing
    if (window.innerWidth > 749) return;

    const items = root.querySelectorAll(".ck-mobile-item");

    if (!items.length) return;

    items.forEach((item) => {

        const button = item.querySelector(".ck-mobile-btn");

        if (!button) return;

        button.addEventListener("click", function () {

            const isOpen = item.classList.contains("active");

            items.forEach(i => i.classList.remove("active"));

            if (!isOpen) {
                item.classList.add("active");
            }

        });

    });

}

  /* ============================================================
     FAQ ACCORDION — only one open at a time
     ============================================================ */
  function initFaq(root) {
    const items = Array.from(root.querySelectorAll('[data-ck-faq-item]'));
    if (!items.length) return;

    items.forEach((item) => {
      const question = item.querySelector('[data-ck-faq-question]');
      const answer = item.querySelector('[data-ck-faq-answer]');
      if (!question || !answer) return;

      question.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        items.forEach((other) => {
          other.classList.remove('is-open');
          const otherAnswer = other.querySelector('[data-ck-faq-answer]');
          if (otherAnswer) otherAnswer.style.maxHeight = null;
          const otherQ = other.querySelector('[data-ck-faq-question]');
          if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('is-open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
          question.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }
  

  /* ============================================================
     DELIVERY PINCODE CHECKER (placeholder logic)
     ============================================================ */
  function initPincodeChecker(root) {
    const btn = root.querySelector('[data-ck-check-pincode]');
    const input = root.querySelector('[data-ck-pincode]');
    const result = root.querySelector('[data-ck-delivery-result]');
    if (!btn || !input || !result) return;

    btn.addEventListener('click', () => {
      const val = input.value.trim();
      if (!/^\d{6}$/.test(val)) {
        result.hidden = false;
        result.textContent = 'Please enter a valid 6-digit pincode.';
        return;
      }
      result.hidden = false;
      result.textContent = 'Delivery available — usually arrives in 3–5 business days.';
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        btn.click();
      }
    });
  }

  /* ============================================================
     BUTTON RIPPLE EFFECT
     ============================================================ */
  function initRipple(root) {
    root.querySelectorAll('.ck-ripple').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        const rect = btn.getBoundingClientRect();
        const circle = document.createElement('span');
        const size = Math.max(rect.width, rect.height);
        circle.className = 'ck-ripple-circle';
        circle.style.width = circle.style.height = size + 'px';
        circle.style.left = (e.clientX - rect.left - size / 2) + 'px';
        circle.style.top = (e.clientY - rect.top - size / 2) + 'px';
        btn.appendChild(circle);
        window.setTimeout(() => circle.remove(), 650);
      });
    });
  }

  /* ============================================================
     FADE-IN ON SCROLL — single shared IntersectionObserver
     ============================================================ */
  function initFadeInObserver(root) {
    const targets = root.querySelectorAll('.ck-fade-in');
    if (!targets.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    targets.forEach((t) => observer.observe(t));

  }


  document.querySelectorAll('[data-ck-variant-cards]').forEach((wrap) => {
  wrap.addEventListener('click', (e) => {
    const card = e.target.closest('[data-ck-variant-card]');
    if (!card || card.disabled) return;

    const section = wrap.closest('[data-section-id]');
    const variantId = card.dataset.variantId;

    // update selection state
    wrap.querySelectorAll('[data-ck-variant-card]').forEach((c) => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');

    // update hidden input + native select used by the form
    const hiddenInput = section.querySelector('[data-ck-variant-id]');
    if (hiddenInput) hiddenInput.value = variantId;

    const nativeSelect = section.querySelector('[data-ck-native-select]');
    if (nativeSelect) {
      nativeSelect.value = variantId;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // update price row directly (in case no other listener does it)
    const priceEl = section.querySelector('[data-ck-price]');
    if (priceEl) priceEl.textContent = card.dataset.variantPrice;
  });
});
  /* ============================================================
     RELATED / RECENTLY VIEWED SLIDER CONTROLS
     ============================================================ */
  function initRelatedSliders(root) {
    root.querySelectorAll('[data-ck-slider]').forEach((slider) => {
      const track = slider.querySelector('.ck-slider-track');
      const prev = slider.parentElement.querySelector('[data-ck-slider-prev]');
      const next = slider.parentElement.querySelector('[data-ck-slider-next]');
      if (!track) return;

      function scrollByAmount(dir) {
        const card = track.querySelector('.ck-product-card');
        const amount = card ? card.getBoundingClientRect().width + 20 : 280;
        track.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', () => scrollByAmount(-1));
      if (next) next.addEventListener('click', () => scrollByAmount(1));
    });

    // Quick add buttons on product cards
    root.querySelectorAll('[data-ck-quick-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then((res) => res.json())
          .then(() => {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            const label = btn.textContent;
            btn.textContent = 'Added ✓';
            window.setTimeout(() => { btn.textContent = label; }, 1600);
          })
          .catch(() => {});
      });
    });

    // Wishlist buttons on product cards
    root.querySelectorAll('[data-ck-card-wishlist]').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.classList.toggle('is-active');
      });
    });
  }

  /* ============================================================
     RECENTLY VIEWED — localStorage-backed, debounced write
     ============================================================ */
  function initRecentlyViewed(root, productData) {
    const key = 'ck_recently_viewed';
    const handle = root.dataset.productHandle;
    if (!handle) return;

    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { list = []; }

    list = list.filter((h) => h !== handle);
    list.unshift(handle);
    list = list.slice(0, 8);

    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }

    const section = root.querySelector('[data-ck-recently-viewed]');
    const track = root.querySelector('[data-ck-recently-viewed-track]');
    const others = list.filter((h) => h !== handle);

    if (!section || !track || others.length === 0) return;

    // Wire up Add to Cart / quick-add clicks once, via delegation, since
    // cards are appended asynchronously as each product fetch resolves.
    track.addEventListener('click', handleMiniCardClick);

    // Fetch each product's rendered card via Shopify's product recommendations JSON,
    // debounced with requestIdleCallback where available to avoid blocking the main thread.
    const run = window.requestIdleCallback || window.setTimeout;
    run(() => {
      others.forEach((h) => {
        fetch('/products/' + h + '.js')
          .then((res) => (res.ok ? res.json() : null))
          .then((p) => {
            if (!p) return;
            const card = buildMiniCard(p);
            track.appendChild(card);
            section.hidden = false;
          })
          .catch(() => {});
      });
    });
  }

  /* Same visual + interactive treatment as the "You May Also Like" grid
     (.na-card): quick-view + quick-add icons, price row with compare/
     discount, stock status, and a working Add to Cart button. Single-
     variant products add directly; multi-variant products link through
     to the product page instead (no variant popup available here). */
  function buildMiniCard(product) {
    const mainVariant = (product.variants && product.variants[0]) || {};
    const isMultiVariant = (product.variants || []).length > 1;
    const isAvailable = typeof product.available === 'boolean' ? product.available : !!mainVariant.available;
    const productUrl = '/products/' + product.handle;

    const price = formatMiniMoney(product.price);
    const hasCompare = product.compare_at_price && product.compare_at_price > product.price;
    const comparePrice = hasCompare ? formatMiniMoney(product.compare_at_price) : null;
    const offPct = hasCompare
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : null;

    const imgSrc = product.featured_image || (product.images && product.images[0]) || '';

    const card = document.createElement('div');
    card.className = 'na-card';
    card.dataset.url = productUrl;
    card.dataset.multiVariant = isMultiVariant ? 'true' : 'false';

    card.innerHTML =
      '<div class="na-card-media">' +
        '<div class="na-quick-actions">' +
          '<a href="' + productUrl + '" class="na-icon-btn" aria-label="Quick view">' +
            '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>' +
          '</a>' +
          '<button type="button" class="na-icon-btn na-quick-add" data-variant-id="' + (mainVariant.id || '') + '" aria-label="Add to cart">' +
            '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/></svg>' +
          '</button>' +
        '</div>' +
        '<a href="' + productUrl + '" class="na-slide-wrap">' +
          (imgSrc ? '<img class="na-slide na-slide-active" src="' + imgSrc + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="500" height="500">' : '') +
        '</a>' +
      '</div>' +
      '<div class="na-card-info">' +
        '<a href="' + productUrl + '" class="na-title" title="' + escapeHtml(product.title) + '">' + escapeHtml(product.title) + '</a>' +
        '<div class="na-price-row">' +
          '<span class="na-price">' + price + '</span>' +
          (hasCompare ? '<span class="na-compare">' + comparePrice + '</span><span class="na-off">-' + offPct + '%</span>' : '') +
        '</div>' +
        '<div class="na-stock' + (isAvailable ? '' : ' na-stock-out') + '">' +
          '<span class="na-stock-dot"></span>' + (isAvailable ? 'In stock' : 'Out of stock') +
        '</div>' +
        '<button type="button" class="na-addcart-btn" data-variant-id="' + (mainVariant.id || '') + '"' + (isAvailable ? '' : ' disabled') + '>' +
          (isAvailable ? 'Add to Cart' : 'Sold Out') +
        '</button>' +
      '</div>';

    return card;
  }

  function handleMiniCardClick(e) {
    const quickBtn = e.target.closest('.na-quick-add');
    const addBtn = e.target.closest('.na-addcart-btn');
    const btn = quickBtn || addBtn;
    if (!btn) return;

    e.preventDefault();

    const card = btn.closest('.na-card');
    const variantId = btn.dataset.variantId;
    const isMultiVariant = card && card.dataset.multiVariant === 'true';
    const url = card ? card.dataset.url : null;

    // No variant popup available in this card — send multi-variant
    // products to the product page instead of guessing which variant to add.
    if (!variantId || isMultiVariant) {
      if (url) window.location.href = url;
      return;
    }

    btn.disabled = true;
    const originalText = btn.textContent;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then((res) => res.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (quickBtn) {
          btn.classList.add('na-added');
          window.setTimeout(() => btn.classList.remove('na-added'), 1500);
        } else {
          btn.textContent = 'Added ✓';
          window.setTimeout(() => { btn.textContent = originalText; }, 1500);
        }
      })
      .catch(() => {
        if (url) window.location.href = url;
      })
      .finally(() => { btn.disabled = false; });
  }

  function formatMiniMoney(cents) {
    return '₹' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
})();
